import { Suspense } from "react";
import { useMediaQuery } from "react-responsive";
import { NavLink } from "react-router-dom";
import { motion } from "motion/react";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import CartDropdown from "../../components/dropdown/CartDropdown";
import UserDropdown from "../../components/dropdown/UserDropdown";
import SearchDropdown from "../../components/dropdown/SearchDropdown";
import "./style.scss";
import MobileMenu from "../../components/dropdown/MobileMenu";
import { useUser } from "../../functional-components/UserContext";
import { shopUrl } from "../../config/urls";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import B2BSearch from "../../components/B2BSearch";
import LocDropdown from "../../components/dropdown/LocDropdown";
import TopNavMenuItem from "./TopNavMenuItem";
import classNames from "classnames";
import Icon from "../../components/Icon";
import { useLocSettings } from "../../hooks/SettingsHooks";
import LogoMenu from "./LogoMenu";
import { useOverlayContext } from "../../functional-components/OverlayContext";

const TopNavigation = () => {
  const LOGO_MENU_ITEM_ID = "[logo]";
  const { authenticated, b2b } = useUser();
  const t = useBjelinTranslations();
  const { top_navigation: topNavigation } = useLocSettings();
  const isSmall = useMediaQuery({ maxWidth: 767.98 });
  const { flyoutOpened, overlayOpened } = useOverlayContext();

  const links = topNavigation.menu_items
    .filter(item => item.text !== LOGO_MENU_ITEM_ID)
    .map(link => <TopNavMenuItem {...link} key={link.text} />);
  const logoMenuContent = topNavigation.menu_items.find(
    item => item.text === LOGO_MENU_ITEM_ID
  )?.content;

  const features = useSiteFeatures();

  return (
    <motion.nav
      aria-label="Main"
      className={classNames("top-navigation", {
        "above-overlay": overlayOpened && flyoutOpened
      })}
      id="top-navigation"
      style={{
        top: "var(--padding-top)"
      }}
    >
      <div className="menu-content">
        <div className="columns">
          <div
            className="logo-column"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <LogoMenu content={logoMenuContent} />
          </div>
          {b2b ? (
            <motion.div
              className="links"
              style={{ marginLeft: "42px" }}
              initial={{
                y: "-100%"
              }}
              animate={{
                y: "0%"
              }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              {!isSmall && (
                <NavLink key="shop" to={shopUrl}>
                  {features.shop()
                    ? t("menu-label.shop")
                    : t("menu-label.products")}
                </NavLink>
              )}
              <B2BSearch />
            </motion.div>
          ) : (
            !isSmall && <div className="links">{links}</div>
          )}
          <div className="icons">
            {!b2b && (features.shop() || features.products()) && (
              <div id="search-menu">
                <SearchDropdown />
              </div>
            )}
            {features.shop() && (
              <div id="cart-menu">
                <Suspense
                  fallback={
                    <motion.button
                      key="user-menu-button-spinner"
                      className="nav-link"
                      initial={{
                        opacity: 0,
                        scale: 0.5
                      }}
                      animate={{
                        opacity: 1,
                        scale: 1
                      }}
                      transition={{ type: "spring", stiffness: 1000 }}
                      aria-busy
                    >
                      <Icon name="spinner" className="fa-spin-pulse" />
                    </motion.button>
                  }
                >
                  <CartDropdown />
                </Suspense>
              </div>
            )}
            {(authenticated || features.login()) && (
              <div id="user-menu">
                <Suspense
                  fallback={
                    <motion.button
                      key="user-menu-button-spinner"
                      className="nav-link"
                      initial={{
                        opacity: 0,
                        scale: 0.5
                      }}
                      animate={{
                        opacity: 1,
                        scale: 1
                      }}
                      transition={{ type: "spring", stiffness: 1000 }}
                      aria-busy
                    >
                      <Icon name="spinner" className="fa-pulse" />
                    </motion.button>
                  }
                >
                  <UserDropdown />
                </Suspense>
              </div>
            )}
            <div id="loc-menu">
              <LocDropdown />
            </div>
            {(b2b || isSmall) && (
              <div id="mobile-menu">
                <MobileMenu>{links}</MobileMenu>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default TopNavigation;
